import React from 'react';
import { Drawer, List, Badge, Typography, Flex, Button, Empty, Tag, Grid } from 'antd';
import { Bell, Clock, CheckCircle2, Utensils, X, ChefHat, Package } from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { NotificationData } from 'services/webSocket/WebSocketComponent';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;

interface StaffNotificationsTrayProps {
  open: boolean;
  onClose: () => void;
  notifications: NotificationData[];
  onMarkRead: (id: string) => void;
  onClearAll: () => void;
  translate: (s: string) => string;
}

const StaffNotificationsTray: React.FC<StaffNotificationsTrayProps> = ({
  open,
  onClose,
  notifications,
  onMarkRead,
  onClearAll,
  translate
}) => {
  const screens = Grid.useBreakpoint();
  const getIcon = (icon?: string) => {
    switch (icon) {
      case 'utensils': return <Utensils size={18} color="#3b82f6" />;
      case 'chef-hat': return <ChefHat size={18} color="#f97316" />;
      case 'package': return <Package size={18} color="#ef4444" />;
      default: return <Bell size={18} color="#64748b" />;
    }
  };

  const getBgColor = (type: string) => {
    if (type.includes('READY')) return 'rgba(59, 130, 246, 0.05)';
    if (type.includes('NEW')) return 'rgba(249, 115, 22, 0.05)';
    return 'transparent';
  };

  return (
    <Drawer
      title={
        <Flex justify="space-between" align="center" style={{ width: '100%' }}>
          <Title level={4} style={{ margin: 0, color: '#0f172a' }}>{translate('Notifications')}</Title>
          <Button type="text" onClick={onClearAll} style={{ color: '#64748b', fontSize: '12px' }}>{translate('Clear All')}</Button>
        </Flex>
      }
      placement="right"
      onClose={onClose}
      open={open}
      width={screens.xs ? '100%' : 400}
      styles={{
        header: { background: '#ffffff', borderBottom: '1px solid #e2e8f0' },
        body: { background: '#ffffff', padding: 0 }
      }}
      closeIcon={<X color="#475569" size={20} />}
    >
      <List
        dataSource={notifications}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span style={{ color: '#94a3b8' }}>{translate('No notifications yet')}</span>} /> }}
        renderItem={(item) => (
          <List.Item
            onClick={() => onMarkRead(item.id)}
            style={{
              padding: '20px 24px',
              borderBottom: '1px solid #f1f5f9',
              cursor: 'pointer',
              background: item.read ? 'transparent' : getBgColor(item.type),
              transition: 'all 0.3s ease',
              position: 'relative'
            }}
          >
            {!item.read && (
                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', background: '#3b82f6' }} />
            )}
            <List.Item.Meta
              avatar={
                <div style={{ padding: '10px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #f1f5f9' }}>
                  {getIcon(item.icon)}
                </div>
              }
              title={
                <Flex justify="space-between" align="center">
                  <Text strong style={{ color: item.read ? '#64748b' : '#0f172a', fontSize: '14px' }}>{translate(item.title)}</Text>
                  <Text style={{ color: '#94a3b8', fontSize: '11px' }}>
                    {dayjs(item.timestamp).fromNow()}
                  </Text>
                </Flex>
              }
              description={
                <div style={{ marginTop: '4px' }}>
                  <Text style={{ color: item.read ? '#94a3b8' : '#475569', fontSize: '13px', lineHeight: 1.4 }}>
                    {item.message}
                  </Text>
                  {item.type === 'KITCHEN_ORDER_READY' && !item.read && (
                      <div style={{ marginTop: '12px' }}>
                          <Button size="small" type="primary" icon={<CheckCircle2 size={14} />} style={{ borderRadius: '8px', fontSize: '11px', background: '#3b82f6', border: 'none', height: '32px', fontWeight: 600 }}>
                              {translate('Confirm Pickup')}
                          </Button>
                      </div>
                  )}
                </div>
              }
            />
          </List.Item>
        )}
      />
    </Drawer>
  );
};

export default StaffNotificationsTray;
